/**
 * Kunden-Seite: Ticket-Stand ansehen + Antworten hinzufuegen.
 * Kein Login. Token aus der URL. Interne Notizen sind fuer den Kunden NICHT sichtbar
 * (Backend filtert internal_note=FALSE).
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Headphones, Send } from 'lucide-react';

interface PublicTicket {
  id: string;
  ticket_number: string;
  customer_email: string;
  customer_name: string;
  subject: string;
  description: string;
  category: string | null;
  priority: string;
  status: string;
  created_at: string;
  updated_at: string;
}
interface PublicMessage {
  id: string;
  author_type: 'customer' | 'agent' | 'system';
  body: string;
  created_at: string;
}

const STATUS_LABEL: Record<string, string> = {
  new: 'Eingegangen',
  triaged: 'Eingegangen',
  assigned: 'Zugewiesen',
  in_progress: 'In Bearbeitung',
  waiting_customer: 'Wartet auf Ihre Antwort',
  resolved: 'Geloest',
  closed: 'Abgeschlossen',
  cancelled: 'Storniert',
};

export default function PublicTicketPage() {
  const { token } = useParams<{ token: string }>();
  const [ticket, setTicket] = useState<PublicTicket | null>(null);
  const [messages, setMessages] = useState<PublicMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError('');
    try {
      const r = await fetch(`/api/public/service-desk/ticket/${encodeURIComponent(token)}`);
      const j = await r.json() as { success: boolean; data?: { ticket: PublicTicket; messages: PublicMessage[] }; error?: string };
      if (!r.ok || !j.success || !j.data) {
        setError(j.error ?? `Fehler ${r.status}`);
        return;
      }
      setTicket(j.data.ticket);
      setMessages(j.data.messages);
    } catch (err) {
      setError((err as Error).message);
    } finally { setLoading(false); }
  }, [token]);
  useEffect(() => { void load(); }, [load]);

  const send = async () => {
    if (!token || !reply.trim()) return;
    setSending(true); setError('');
    try {
      const r = await fetch(`/api/public/service-desk/ticket/${encodeURIComponent(token)}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: reply.trim() }),
      });
      const j = await r.json() as { success: boolean; error?: string };
      if (!r.ok || !j.success) {
        setError(j.error ?? `Fehler ${r.status}`);
        return;
      }
      setReply('');
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally { setSending(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-400">
        <Loader2 size={16} className="animate-spin mr-2" /> Laedt...
      </div>
    );
  }
  if (!ticket) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-5 max-w-md">
          <div className="text-rose-300 font-medium mb-1">Ticket nicht abrufbar</div>
          <p className="text-sm text-slate-300">{error || 'Der Link ist abgelaufen oder ungueltig.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="flex items-center gap-2">
          <Headphones size={20} className="text-brand-400" />
          <h1 className="text-lg font-semibold text-slate-100">Service-Anfrage {ticket.ticket_number}</h1>
        </div>

        <div className="rounded-lg border border-slate-800 bg-slate-900 p-4">
          <div className="text-xs text-slate-500 mb-1">Status</div>
          <div className="text-base text-slate-100 font-medium">{STATUS_LABEL[ticket.status] ?? ticket.status}</div>
          <div className="text-xs text-slate-500 mt-3">Betreff</div>
          <div className="text-slate-100">{ticket.subject}</div>
          <div className="text-xs text-slate-500 mt-3">Ihre Beschreibung</div>
          <p className="text-sm text-slate-200 whitespace-pre-wrap mt-1">{ticket.description}</p>
        </div>

        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-slate-300">Verlauf</h2>
          {messages.length === 0 && (
            <div className="text-sm text-slate-500 italic py-3">Noch keine Nachrichten.</div>
          )}
          {messages.map((m) => (
            <div
              key={m.id}
              className={`rounded-lg border p-3 ${
                m.author_type === 'customer'
                  ? 'bg-slate-900 border-slate-800'
                  : 'bg-brand-500/5 border-brand-500/30'
              }`}
            >
              <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
                <span className="font-medium">
                  {m.author_type === 'customer' ? 'Sie' : 'Support-Team'}
                </span>
                <span>{new Date(m.created_at).toLocaleString('de-DE')}</span>
              </div>
              <p className="text-sm text-slate-200 whitespace-pre-wrap">{m.body}</p>
            </div>
          ))}
        </div>

        {!['closed', 'cancelled'].includes(ticket.status) && (
          <div className="rounded-lg border border-slate-800 bg-slate-900 p-3">
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Ergaenzung oder Antwort…"
              rows={4}
              className="w-full px-3 py-2 rounded-md border border-slate-700 bg-slate-950 text-slate-100 text-sm"
            />
            <div className="flex justify-end mt-2">
              <button
                type="button"
                onClick={() => void send()}
                disabled={sending || !reply.trim()}
                className="inline-flex items-center gap-2 px-4 py-1.5 text-sm rounded-md bg-brand-600 text-primary-foreground hover:bg-brand-500 disabled:opacity-50"
              >
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Senden
              </button>
            </div>
          </div>
        )}

        {error && <div className="text-sm text-rose-400">{error}</div>}
      </div>
    </div>
  );
}
